import React, { createContext, useContext, useMemo, useReducer } from "react";

import { audienceReducer } from "./AudienceReducer";
import { Action, AudienceState, initialState } from "./types";

// State and dispatch are separate so components that only dispatch don't
// re-render when the audience state changes.
const AudienceStateContext = createContext<AudienceState | undefined>(undefined);
const AudienceDispatchContext = createContext<React.Dispatch<Action> | undefined>(undefined);

export const AudienceProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(audienceReducer, initialState);

  return (
    <AudienceDispatchContext.Provider value={dispatch}>
      <AudienceStateContext.Provider value={state}>
        {children}
      </AudienceStateContext.Provider>
    </AudienceDispatchContext.Provider>
  );
};

export const useAudienceState = () => {
  const state = useContext(AudienceStateContext);
  if (state === undefined) {
    throw new Error("useAudienceState must be used within an AudienceProvider");
  }
  return state;
};

export const useAudienceDispatch = () => {
  const dispatch = useContext(AudienceDispatchContext);
  if (dispatch === undefined) {
    throw new Error("useAudienceDispatch must be used within an AudienceProvider");
  }
  return dispatch;
};

/**
 * State and dispatch together. Prefer `useAudienceDispatch` when you only
 * dispatch, as this re-renders on every audience state change.
 */
export const useAudienceContext = () => {
  const state = useAudienceState();
  const dispatch = useAudienceDispatch();
  return useMemo(() => ({ state, dispatch }), [state, dispatch]);
};
